var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement } from "lit";
import { property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import "../radio/radio.js";
import { activateI18n } from "../i18n";
import { styles as hostStyles } from "../radio/host-styles.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
// eslint-disable-next-line
// @ts-ignore
import { styles } from "./radio-group-styles.js";
activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
const REQUIRED_MESSAGE = () => i18n._({
    id: "radio-group.validation.required",
    message: "Please select an option.",
    comment: "Shown when required radio group has no selections",
});
/*
 * Radios allow users to select a single option from a list of choices.
 *
 * Use with `w-radio`.
 *
 * @slot label - Alternative to the `label` attribute should you need custom HTML.
 * @slot help-text - Alternative to the `help-text` attribute should you need custom HTML.
 *
 * ## Architecture Note
 * This component uses semantic <fieldset> element for grouping radio controls, which
 * provides better accessibility and follows HTML best practices. Note that w-checkbox-group
 * currently uses <div> instead - this inconsistency exists for historical reasons and may
 * be addressed in a future major version to align both on the more semantic approach.
 *
 * TODO: Align w-checkbox-group to also use <fieldset> in a future major version after
 * assessing backwards compatibility implications (CSS selectors, etc.).
 */
/**
 * Radios allow users to select a single option from a list of choices.
 *
 * Wrap individual radio components in a radio group.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/radio/frameworks/elements)
 *
 * @slot label - Alternative to the `label` attribute should you need custom HTML.
 * @slot help-text - Alternative to the `help-text` attribute should you need custom HTML.
 */
export class WarpRadioGroup extends FormControlMixin(LitElement) {
    static { this.styles = [hostStyles, styles]; }
    /** @internal */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    constructor() {
        super();
        /** @internal */
        this.hasInteracted = false;
        this.hasWarnedMissingName = false;
        this.autoTabIndex = false;
        /**
         * Label for the radio group.
         */
        this.label = "";
        /**
         * Help text for the radio group.
         *
         * If you set `required` and `invalid` the group gets a default error message, but you can override it with this attribute.
         */
        this.helpText = "";
        /**
         * Whether to show optional text next to the label.
         */
        this.optional = false;
        /**
         * Marks the radio group as invalid.
         */
        this.invalid = false;
        /**
         * The [name](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#name) of the select when submitting the form.
         */
        this.name = null;
        /**
         * Disables the radio group and all child radios.
         */
        this.disabled = false;
        /**
         * Makes selecting a radio in the the group required.
         */
        this.required = false;
        this.defaultCheckedValue = undefined;
        this.slottedHelpText = null;
        this.nameManagedRadios = new WeakSet();
        this.disabledManagedRadios = new WeakSet();
        this.handleRadioClick = (e) => {
            const clickedRadio = e.target.closest("w-radio");
            if (!clickedRadio || clickedRadio.disabled || this.disabled)
                return;
            const oldValue = this.getCheckedValue();
            const radios = this.getAllRadios();
            this.selectSingleRadio(clickedRadio, radios);
            const newValue = this.getCheckedValue();
            if (newValue !== oldValue) {
                this.updateComplete.then(this.emitSelectionChange);
            }
        };
        this.handleInvalid = (event) => {
            event.preventDefault();
            this.hasInteracted = true;
            this.updateValidity();
        };
        this.handleHelpTextSlotChange = () => {
            this.syncSlottedHelpText();
            this.requestUpdate();
        };
        this.handleI18nChange = () => {
            this.requestUpdate();
        };
        this.emitSelectionChange = () => {
            this.hasInteracted = true;
            this.syncFormValue();
            this.updateValidity();
            this.requestUpdate();
            this.dispatchEvent(new InputEvent("input", { bubbles: true, composed: true }));
            this.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
        };
        this.captureDefaultSelection = () => {
            if (this.defaultCheckedValue === undefined) {
                this.defaultCheckedValue = this.getCheckedValue();
            }
        };
        this.addEventListener("keydown", this.handleKeyDown);
        this.addEventListener("click", this.handleRadioClick);
        this.addEventListener("invalid", this.handleInvalid);
    }
    /** @internal */
    get validationTarget() {
        const radio = this.querySelector(":is(w-radio):not([disabled])");
        return radio ?? undefined;
    }
    connectedCallback() {
        super.connectedCallback();
        this.syncSlottedHelpText();
        this.syncFormValue();
        this.updateValidity();
        this.unsubscribeI18n = i18n.on("change", this.handleI18nChange);
        this.warnIfMissingName();
        this.updateComplete.then(this.captureDefaultSelection);
    }
    disconnectedCallback() {
        this.unsubscribeI18n?.();
        this.unsubscribeI18n = undefined;
        super.disconnectedCallback();
    }
    async updated(changedProperties) {
        const shouldResync = changedProperties.has("disabled") ||
            changedProperties.has("name") ||
            changedProperties.has("required") ||
            changedProperties.has("invalid") ||
            changedProperties.has("helpText");
        if (shouldResync) {
            // Keep host validity/UI in sync in the same update cycle, before async child sync.
            this.syncFormValue();
            this.updateValidity();
            this.syncRadioElements();
            this.syncFormValue();
            this.updateValidity();
        }
    }
    /** @internal */
    resetFormControl() {
        const defaultValue = this.defaultCheckedValue ?? null;
        this.getAllRadios().forEach((radio) => {
            radio.checked = defaultValue ? radio.value === defaultValue : false;
        });
        this.syncRadioElements();
        this.syncFormValue();
        this.updateValidity();
    }
    getAllRadios() {
        return [...this.querySelectorAll("w-radio")];
    }
    getCheckedValue() {
        const checked = this.getAllRadios().find((radio) => radio.checked);
        return checked?.value ?? null;
    }
    getEnabledRadios(radios = this.getAllRadios()) {
        return radios.filter((radio) => !radio.disabled);
    }
    selectSingleRadio(selected, radios = this.getAllRadios()) {
        radios.forEach((radio) => {
            const isSelected = radio === selected;
            radio.checked = isSelected;
            // Use non-reflecting property to avoid DOM changes during hydration
            radio._groupTabIndex = isSelected ? 0 : -1;
        });
    }
    handleLabelClick() {
        this.focus();
    }
    async syncRadioElements() {
        const radios = this.getAllRadios();
        radios.forEach((radio, index) => {
            radio.toggleAttribute("data-w-radio-first", index === 0);
            radio.toggleAttribute("data-w-radio-inner", index !== 0 && index !== radios.length - 1);
            radio.toggleAttribute("data-w-radio-last", index === radios.length - 1);
            this.syncRadioDisabledState(radio);
            if (this.name) {
                if (!radio.getAttribute("name") || this.nameManagedRadios.has(radio)) {
                    radio.setAttribute("name", this.name);
                    this.nameManagedRadios.add(radio);
                }
            }
            else if (this.nameManagedRadios.has(radio)) {
                radio.removeAttribute("name");
                this.nameManagedRadios.delete(radio);
            }
        });
        await Promise.all(radios.map(async (radio) => radio.updateComplete));
        this.normalizeCheckedRadios(radios);
        this.syncTabOrder(radios);
    }
    syncRadioDisabledState(radio) {
        if (this.disabled) {
            if (!radio.disabled) {
                radio.disabled = true;
                this.disabledManagedRadios.add(radio);
            }
            return;
        }
        if (this.disabledManagedRadios.has(radio)) {
            radio.disabled = false;
            this.disabledManagedRadios.delete(radio);
        }
    }
    syncTabOrder(radios) {
        // Use non-reflecting _groupTabIndex property to avoid DOM changes during hydration
        if (this.disabled) {
            radios.forEach((radio) => (radio._groupTabIndex = -1));
            return;
        }
        const enabledRadios = this.getEnabledRadios(radios);
        const checkedRadio = enabledRadios.find((radio) => radio.checked);
        if (enabledRadios.length > 0) {
            if (checkedRadio) {
                enabledRadios.forEach((radio) => (radio._groupTabIndex = radio.checked ? 0 : -1));
            }
            else {
                enabledRadios.forEach((radio, index) => (radio._groupTabIndex = index === 0 ? 0 : -1));
            }
        }
        radios
            .filter((radio) => radio.disabled)
            .forEach((radio) => (radio._groupTabIndex = -1));
    }
    handleKeyDown(event) {
        if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(event.key) ||
            this.disabled)
            return;
        const allRadios = this.getAllRadios();
        const radios = this.getEnabledRadios(allRadios);
        if (radios.length <= 0)
            return;
        event.preventDefault();
        const oldValue = this.getCheckedValue();
        const checkedRadio = radios.find((radio) => radio.checked) ?? radios[0];
        const incr = event.key === " "
            ? 0
            : ["ArrowUp", "ArrowLeft"].includes(event.key)
                ? -1
                : 1;
        let index = radios.indexOf(checkedRadio) + incr;
        if (index < 0)
            index = radios.length - 1;
        if (index >= radios.length)
            index = 0;
        this.selectSingleRadio(radios[index], allRadios);
        radios[index].focus();
        const newValue = this.getCheckedValue();
        if (newValue !== oldValue) {
            this.updateComplete.then(this.emitSelectionChange);
        }
    }
    /** @internal */
    focus(options) {
        if (this.disabled)
            return;
        const radios = this.getAllRadios();
        const checked = radios.find((radio) => radio.checked);
        const firstEnabledRadio = radios.find((radio) => !radio.disabled);
        const radioToFocus = checked || firstEnabledRadio;
        if (radioToFocus) {
            radioToFocus.focus(options);
        }
    }
    /** @internal */
    checkValidity() {
        this.updateValidity();
        return this.internals.checkValidity();
    }
    /** @internal */
    reportValidity() {
        this.hasInteracted = true;
        this.updateValidity();
        return this.internals.checkValidity();
    }
    hasSlottedContent(name) {
        if (this.querySelector(`[slot="${name}"]`))
            return true;
        const slot = this.shadowRoot?.querySelector(`slot[name="${name}"]`);
        if (!slot)
            return false;
        return slot.assignedNodes({ flatten: true }).some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE)
                return true;
            if (node.nodeType === Node.TEXT_NODE)
                return Boolean(node.textContent?.trim());
            return false;
        });
    }
    syncFormValue() {
        this.setValue(null);
    }
    syncSlottedHelpText() {
        const slottedHelpText = this.querySelector('[slot="help-text"]');
        this.slottedHelpText = slottedHelpText?.textContent?.trim() || null;
    }
    updateValidity() {
        this.warnIfMissingName();
        const isRequiredInvalid = this.required && !this.getCheckedValue();
        const showRequiredError = isRequiredInvalid && this.hasInteracted;
        const externalInvalid = this.invalid || this.hasAttribute("invalid");
        const showInvalidUi = externalInvalid || showRequiredError;
        this.toggleAttribute("data-show-error", showInvalidUi);
        if (this.disabled) {
            this.internals.setValidity({});
            this.syncChildInvalid(false);
            this.syncHostTabIndex(false);
            return;
        }
        this.syncHostTabIndex(showInvalidUi);
        if (isRequiredInvalid) {
            this.setValidityState({ valueMissing: true });
            this.syncChildInvalid(showInvalidUi);
            return;
        }
        if (externalInvalid) {
            this.setValidityState({ customError: true });
            this.syncChildInvalid(true);
            return;
        }
        this.internals.setValidity({});
        this.syncChildInvalid(false);
    }
    normalizeCheckedRadios(radios) {
        const checkedRadio = radios.find((radio) => radio.checked);
        if (!checkedRadio)
            return;
        radios.forEach((radio) => {
            if (radio !== checkedRadio)
                radio.checked = false;
        });
    }
    syncChildInvalid(isInvalid) {
        this.getAllRadios().forEach((radio) => {
            radio.invalid = isInvalid;
        });
    }
    setValidityState(state) {
        const anchor = this.validationTarget;
        this.internals.setValidity(state, " ", anchor ?? undefined);
    }
    syncHostTabIndex(shouldBeFocusable) {
        const hasTabIndexAttr = this.hasAttribute("tabindex");
        if (hasTabIndexAttr && !this.autoTabIndex)
            return;
        if (shouldBeFocusable) {
            this.setAttribute("tabindex", "0");
            this.autoTabIndex = true;
            return;
        }
        if (this.autoTabIndex) {
            this.removeAttribute("tabindex");
            this.autoTabIndex = false;
        }
    }
    warnIfMissingName() {
        if (this.hasWarnedMissingName)
            return;
        if (!this.internals.form)
            return;
        if (this.name && this.name.trim().length > 0)
            return;
        console.warn('w-radio-group: "name" is required for form submission.');
        this.hasWarnedMissingName = true;
    }
    render() {
        const hasLabelSlot = this.hasSlottedContent("label");
        const hasHelpTextSlot = this.hasSlottedContent("help-text");
        const hasLabel = this.label ? true : !!hasLabelSlot;
        const hasHelpText = this.helpText ? true : !!hasHelpTextSlot;
        const isRequiredInvalid = this.required && !this.getCheckedValue();
        const showRequiredError = isRequiredInvalid && this.hasInteracted;
        const externalInvalid = this.invalid || this.hasAttribute("invalid");
        const showInvalidError = externalInvalid || showRequiredError;
        const helpText = showInvalidError
            ? externalInvalid && this.helpText
                ? this.helpText
                : REQUIRED_MESSAGE()
            : this.helpText;
        const shouldShowHelpText = showInvalidError || hasHelpText;
        const labelledBy = hasLabel ? "label" : undefined;
        const describedBy = shouldShowHelpText ? "help-text" : undefined;
        const helpTextAriaLabel = this.slottedHelpText || undefined;
        return html `
			<fieldset
				part="form-control"
				role="radiogroup"
				aria-labelledby=${ifDefined(labelledBy)}
				aria-describedby=${ifDefined(describedBy)}
				aria-errormessage="error-message"
				aria-invalid=${showInvalidError ? "true" : undefined}
			>
				${hasLabel
            ? html `
							<label
								part="form-control-label"
								id="label"
								@click=${this.handleLabelClick}
							>
								<slot name="label">${this.label}</slot>
								${this.optional && !this.required
                ? html `<span class="optional">
											${i18n._({
                    id: "radio-group.label.optional",
                    message: "Optional",
                    comment: "Shown behind label when marked as optional",
                })}
										</span>`
                : null}
							</label>
						`
            : null}

				<slot
					part="form-control-input"
					@slotchange=${this.syncRadioElements}
				></slot>

				${shouldShowHelpText
            ? html `
							<div
								id="help-text"
								part="help-text"
								aria-label=${ifDefined(helpTextAriaLabel)}
							>
								<slot
									name="help-text"
									@slotchange=${this.handleHelpTextSlotChange}
									>${helpText}</slot
								>
							</div>
						`
            : null}
			</fieldset>
		`;
    }
}
__decorate([
    state()
], WarpRadioGroup.prototype, "hasInteracted", void 0);
__decorate([
    property()
], WarpRadioGroup.prototype, "label", void 0);
__decorate([
    property({ attribute: "help-text" })
], WarpRadioGroup.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadioGroup.prototype, "optional", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadioGroup.prototype, "invalid", void 0);
__decorate([
    property({ reflect: true })
], WarpRadioGroup.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadioGroup.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadioGroup.prototype, "required", void 0);
/**
 * @deprecated Exported for backwards compatibility only, use `WarpRadioGroup`
 */
export const WRadioGroup = WarpRadioGroup;
if (!customElements.get("w-radio-group")) {
    customElements.define("w-radio-group", WarpRadioGroup);
}
//# sourceMappingURL=radio-group.js.map